import React, { useState, useEffect } from 'react';
import { OPERATORS_BY_TYPE, VALUELESS_OPERATORS, compileRouterLogic } from '../../utilities/routerLogicBuilder.js';
import './routerLogicBuilderModal.css';

const RouterLogicBuilderModal = ({
  visible,
  onClose,
  fieldOptions = [],       // [{ name, type }]
  branchOptions = [],      // ['pass', 'fail']
  initialConditions = [],  // [{ field, operator, value, branch }]
  initialElseBranch = '',
  onApply,                 // (routerLogicString, conditions, elseBranch) => void
}) => {
  const [conditions, setConditions] = useState([]);
  const [elseBranch, setElseBranch] = useState('');

  useEffect(() => {
    if (visible) {
      setConditions(initialConditions.length > 0 ? initialConditions : [
        { field: fieldOptions[0]?.name || '', operator: '==', value: '', branch: branchOptions[0] || '' }
      ]);
      setElseBranch(initialElseBranch || branchOptions[branchOptions.length - 1] || '');
    }
  }, [visible]);

  if (!visible) return null;

  const fieldTypeMap = Object.fromEntries(fieldOptions.map(f => [f.name, f.type || 'string']));

  const updateCondition = (index, patch) => {
    setConditions(prev => prev.map((c, i) => {
      if (i !== index) return c;
      const updated = { ...c, ...patch };
      if (patch.field) {
        const newType = fieldTypeMap[patch.field] || 'string';
        const validOps = OPERATORS_BY_TYPE[newType].map(o => o.value);
        if (!validOps.includes(updated.operator)) updated.operator = validOps[0];
      }
      return updated;
    }));
  };

  const addCondition = () => {
    setConditions(prev => [
      ...prev,
      { field: fieldOptions[0]?.name || '', operator: '==', value: '', branch: branchOptions[0] || '' }
    ]);
  };

  const removeCondition = (index) => {
    setConditions(prev => prev.filter((_, i) => i !== index));
  };

  const compiledPreview = compileRouterLogic(conditions, elseBranch, fieldTypeMap);

  const handleApply = () => {
    onApply(compiledPreview, conditions, elseBranch);
    onClose();
  };

  return (
    <div className="rlb-overlay">
      <div className="rlb-modal">
        <div className="rlb-header">
          <h3>🧩 Router Logic Builder</h3>
          <button className="rlb-close-btn" onClick={onClose}>✖</button>
        </div>

        <div className="rlb-body">
          {fieldOptions.length === 0 && (
            <div className="rlb-warning">
              ⚠️ No Agent State fields found. Wire this graph to an Agent State component with defined fields first.
            </div>
          )}
          {branchOptions.length === 0 && (
            <div className="rlb-warning">
              ⚠️ No branches found. Wire BRANCH_OUT to target nodes and label each wire first.
            </div>
          )}

          <div className="rlb-conditions">
            {conditions.map((cond, i) => {
              const fieldType = fieldTypeMap[cond.field] || 'string';
              const operators = OPERATORS_BY_TYPE[fieldType] || OPERATORS_BY_TYPE.string;
              const needsValue = !VALUELESS_OPERATORS.includes(cond.operator);

              return (
                <div key={i} className="rlb-condition-row">
                  <span className="rlb-condition-label">{i === 0 ? 'if' : 'else if'}</span>

                  <select className="rlb-select" value={cond.field}
                    onChange={(e) => updateCondition(i, { field: e.target.value })}>
                    {fieldOptions.map(f => (
                      <option key={f.name} value={f.name}>{f.name} ({f.type || 'string'})</option>
                    ))}
                  </select>

                  <select className="rlb-select" value={cond.operator}
                    onChange={(e) => updateCondition(i, { operator: e.target.value })}>
                    {operators.map(op => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </select>

                  {needsValue && (
                    <input
                      className="rlb-input"
                      type={fieldType === 'number' ? 'number' : 'text'}
                      value={cond.value}
                      placeholder="value"
                      onChange={(e) => updateCondition(i, { value: e.target.value })}
                    />
                  )}

                  <span className="rlb-condition-label">then →</span>

                  <select className="rlb-select" value={cond.branch}
                    onChange={(e) => updateCondition(i, { branch: e.target.value })}>
                    <option value="" disabled>— branch —</option>
                    {branchOptions.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>

                  <button
                    className="rlb-remove-btn"
                    onClick={() => removeCondition(i)}
                    disabled={conditions.length === 1}
                    title="Remove condition"
                  >
                    ✖
                  </button>
                </div>
              );
            })}
          </div>

          <button className="rlb-add-btn" onClick={addCondition}>➕ Add Condition</button>

          <div className="rlb-else-row">
            <span className="rlb-condition-label">else →</span>
            <select className="rlb-select" value={elseBranch} onChange={(e) => setElseBranch(e.target.value)}>
              <option value="" disabled>— branch —</option>
              {branchOptions.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>

          <div className="rlb-preview">
            <label className="rlb-preview-label">Compiled Python:</label>
            <code className="rlb-preview-code">{compiledPreview}</code>
          </div>
        </div>

        <div className="rlb-footer">
          <button className="rlb-cancel-btn" onClick={onClose}>Cancel</button>
          <button
            className="rlb-apply-btn"
            onClick={handleApply}
            disabled={fieldOptions.length === 0 || branchOptions.length === 0 || !elseBranch}
          >
            ✅ Apply to Router Logic
          </button>
        </div>
      </div>
    </div>
  );
};

export default RouterLogicBuilderModal;